import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {useTranslation} from '@/translations/i18n'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {Label} from '@/components/ui/label/Label'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {ObjectKeys, workServiceToLabel} from '@/utilities/helpers'
import {RadioField} from '@components/commons/radio-field/RadioField.tsx'
import {UserOperatorFormValidationSchema} from '@/features/users/types'
import {UserInputSelectSpecialization} from '@/features/users/components/user-input-select-specialization/UserInputSelectSpecialization'
import {UserInputFmEvaluation} from '@/features/users/components/user-input-fm-evaluation/UserInputFmEvaluation'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {FormFieldBox} from '@/components/ui/form-atoms/FormAtoms'
import {InputDate} from '@/components/commons/input-date/InputDate'
import dayjs from 'dayjs'

export const UserOperatorForm = () => {
    const {t} = useTranslation()

    // methods
    const form = useFormContext<UserOperatorFormValidationSchema>()

    // form observer
    const specialization = useWatch({name: 'specialization', control: form.control})
    const fm07Doc = useWatch({name: 'fm07EvaluationAttachment', control: form.control})
    const fm08Doc = useWatch({name: 'fm08EvaluationAttachment', control: form.control})

    // side effects
    const onChangeSpecializationSideEffects = () => {
        form.setValue('fm07Evaluation', null, {shouldDirty: true})
        form.setValue('fm07EvaluationAttachment', null, {shouldDirty: true})
        form.setValue('fm08Evaluation', null, {shouldDirty: true})
        form.setValue('fm08EvaluationAttachment', null, {shouldDirty: true})
        form.setValue('fm08EvaluationLastUpdate', new Date(), {shouldDirty: true})
        form.setValue('patientAgeRange', null, {shouldDirty: true})
        form.setValue('workSector', null, {shouldDirty: true})
        form.setValue('qualifications', null, {shouldDirty: true})
        form.setValue('registrationNumber', null, {shouldDirty: true})
        form.setValue('registrationLocation', null, {shouldDirty: true})
        form.setValue('registrationDate', null, {shouldDirty: true})
        form.setValue('ecmScore', null, {shouldDirty: true})
        form.setValue('curriculumAttachment', null, {shouldDirty: true})
        form.setValue('curriculumLastUpdate', null, {shouldDirty: true})
        form.setValue('professionalInsuranceLastVerify', null, {shouldDirty: true})
        form.setValue('absenceOfIncompatibilityLastVerify', null, {shouldDirty: true})
        form.clearErrors(['patientAgeRange', 'workSector'])
    }

    return (
        <>
            <GridItem startColumn={1} endColumn={4}>
                <Controller
                    control={form.control}
                    name="specialization"
                    render={({field}) => (
                        <UserInputSelectSpecialization
                            label={t('commons:specialization')}
                            value={field.value ?? null}
                            placeholder={t('commons:select')}
                            errorMessage={form.formState.errors.specialization?.message}
                            onChange={value => {
                                field.onChange(value)
                                onChangeSpecializationSideEffects()
                            }}
                        />
                    )}
                />
            </GridItem>

            {specialization ? (
                specialization.name == 'Amministrativo' ? (
                    <GridItem startColumn={4} endColumn={7}>
                        <Controller
                            control={form.control}
                            name="fm07Evaluation"
                            render={({field}) => (
                                <UserInputFmEvaluation
                                    label={t('users:fm07')}
                                    evaluation={field.value}
                                    document={fm07Doc}
                                    onChangeEvaluation={field.onChange}
                                    onChangeDocument={document =>
                                        form.setValue('fm07EvaluationAttachment', document, {shouldDirty: true})
                                    }
                                    isOptional
                                />
                            )}
                        />
                    </GridItem>
                ) : (
                    <>
                        <GridItem startColumn={4} endColumn={7} gap={4}>
                            <Flexbox direction="column" gap={1.5}>
                                <Label>{t('users:fm08_last_update')}</Label>
                                <Controller
                                    control={form.control}
                                    name="fm08EvaluationLastUpdate"
                                    render={({field}) => (
                                        <InputDate
                                            placeholder="GG/MM/AAAA"
                                            errorMessage={form.formState.errors.fm08EvaluationLastUpdate?.message}
                                            disableAfterDate={dayjs().toDate()}
                                            fullWidth
                                            value={field.value}
                                            onChange={field.onChange}
                                        />
                                    )}
                                />
                            </Flexbox>
                            <Controller
                                control={form.control}
                                name="fm08Evaluation"
                                render={({field}) => (
                                    <UserInputFmEvaluation
                                        label={t('users:fm08')}
                                        evaluation={field.value}
                                        document={fm08Doc}
                                        onChangeEvaluation={field.onChange}
                                        onChangeDocument={document =>
                                            form.setValue('fm08EvaluationAttachment', document, {shouldDirty: true})
                                        }
                                        isOptional
                                    />
                                )}
                            />
                        </GridItem>

                        <GridItem spanColumn={6}>
                            <Flexbox direction="column" gap={1.5} fullWidth>
                                <Label>{t('commons:service')}</Label>
                                <Controller
                                    control={form.control}
                                    name="patientAgeRange"
                                    render={({field}) => (
                                        <Flexbox fullWidth direction="column" gap={1.5}>
                                            <Grid columns={6} gap={4} fullWidth>
                                                {ObjectKeys(workServiceToLabel).map(serviceKey => (
                                                    <FormFieldBox
                                                        key={serviceKey}
                                                        spanColumn={2}
                                                        isSelected={field.value == serviceKey}
                                                        hasError={!!form.formState.errors.patientAgeRange}
                                                    >
                                                        <RadioField
                                                            label={t(workServiceToLabel[serviceKey])}
                                                            name={field.name}
                                                            checked={field.value == serviceKey}
                                                            onChange={field.onChange}
                                                            value={serviceKey}
                                                        />
                                                    </FormFieldBox>
                                                ))}
                                            </Grid>
                                            {form.formState.errors.patientAgeRange?.message && (
                                                <InputHelpText error={form.formState.errors.patientAgeRange.message} />
                                            )}
                                        </Flexbox>
                                    )}
                                />
                            </Flexbox>
                        </GridItem>
                    </>
                )
            ) : null}
        </>
    )
}
